<script>
import { mapActions, mapGetters } from 'vuex'

import Bill from '@components/bill.vue'
import Layout from '@layouts/main.vue'
import PageHeader from '@components/page-header.vue'

export default {
  page: {
    title: 'Bill',
    meta: [{ name: 'description', content: 'The Bill page.' }],
  },
  components: {
    Bill,
    Layout,
    PageHeader,
  },
  data: () => ({
    loading: false,
  }),
  computed: {
    ...mapGetters({
      billLoading: 'payment/getLoading',
      brand: 'contentful/getBrand',
      content: 'contentful/getContent',
      items: 'payment/getItems',
      location: 'location/getLocation',
      locationLoading: 'location/getLoading',
      selectedItems: 'payment/getSelectedItems',
      selectedTotal: 'payment/getSelectedTotal',
      serviceCharge: 'payment/getServiceCharge',
      splitBill: 'payment/getSplitBill',
      splitServiceCharge: 'payment/getSplitServiceCharge',
      tableNumber: 'payment/getTableNumber',
      tip: 'payment/getTip',
      total: 'payment/getTotal',
    }),
    orderBaseUrl() {
      return this.brand?.oatBaseUrl || this.content?.oatBaseUrl
    },
    orderLink() {
      const { orderBaseUrl, slug, table } = this
      return `${orderBaseUrl}${slug}/order?table=${table}`
    },
    orderLinkText() {
      return this.location?.oatLinkText || 'Order'
    },
    showOrderLink() {
      const isEnabled = this.location?.showOatLink === 'Show'
      return this.orderBaseUrl && isEnabled
    },
    slug() {
      return this.$route.params.restaurant
    },
    table() {
      return this.$route.params.tableNumber
    },
  },
  async created() {
    this.loading = true
    if (!this.items) {
      const { restaurant: slug, tableNumber } = this.$route.params
      if (!this.location || slug !== this.location.slug) {
        await this.getLocationFromSlug({ slug })
      }
      if (!this.tableNumber || tableNumber !== this.tableNumber) {
        await this.setTableNumber({ tableNumber })
      }
      await this.getBill({ tableNumber: this.tableNumber })
      if (!this.items) {
        this.setLoading(false)
      }
    }
    this.loading = this.billLoading || this.locationLoading
    this.checkForData()
  },
  methods: {
    ...mapActions({
      checkForData: 'payment/checkForData',
      getBill: 'payment/getBill',
      getLocationFromSlug: 'location/getLocationFromSlug',
      setLoading: 'payment/setLoading',
      setSelectedItems: 'payment/setSelectedItems',
      setSplitBill: 'payment/setSplitBill',
      setTableNumber: 'payment/setTableNumber',
    }),
    splitTheBill() {
      this.setSplitBill({
        splitBill: true,
        items: this.items.filter((item) => !item.paid),
      })
    },
    cancelSplit() {
      this.setSelectedItems({ selectedItems: null })
      this.setSplitBill({ splitBill: false })
    },
    onSelect(item) {
      const selectedItems = this.selectedItems ? [...this.selectedItems] : []

      if (selectedItems.includes(item)) {
        const itemIndex = selectedItems.indexOf(item)
        if (itemIndex > -1) {
          selectedItems.splice(itemIndex, 1)
        }
      } else {
        selectedItems.push(item)
      }

      this.setSelectedItems({ selectedItems })
    },
  },
}
</script>

<template>
  <Layout screen="bill" :loading="loading">
    <PageHeader :page="1" :title="splitBill ? 'Select items' : 'The bill'" />
    <div class="content">
      <Bill
        :items="items"
        :selected-items="selectedItems"
        :selected-total="selectedTotal"
        :service-charge="serviceCharge"
        :split-bill="splitBill"
        :split-service-charge="splitServiceCharge"
        :tip="tip"
        :total="total"
        @selected-item="onSelect"
      />
    </div>
    <div class="footer" :class="$style.footer">
      <div :class="$style.disclaimer">
        Not your bill? Speak to one of our team
      </div>
      <div :class="$style.buttons">
        <a v-if="orderLink && showOrderLink" :href="orderLink">
          <BaseButton :class="$style.button" v-text="orderLinkText" />
        </a>
        <BaseLink
          v-if="!splitBill"
          :to="{ name: serviceCharge ? 'details' : 'tip' }"
        >
          <BaseButton :class="$style.button"> Pay in full </BaseButton>
        </BaseLink>
        <BaseLink
          v-if="splitBill"
          :disabled="!selectedTotal"
          :to="{ name: serviceCharge ? 'details' : 'tip' }"
        >
          <BaseButton :disabled="!selectedTotal" :class="$style.button">
            Pay your Share
          </BaseButton>
        </BaseLink>
        <BaseButton
          v-if="!splitBill"
          :class="$style.button"
          @click="splitTheBill"
        >
          Split the Bill
        </BaseButton>
        <BaseButton v-else :class="$style.button" @click="cancelSplit">
          Cancel the Split
        </BaseButton>
      </div>
    </div>
  </Layout>
</template>

<style lang="scss" module>
@import '@design';

.buttons {
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 1rem 2.5rem 0.5rem;
}

.button {
  margin: 0.5rem 0;
}

.disclaimer {
  @extend %font-secondary;
  color: var(--color-text-secondary);
  border-bottom: #727272 2px dashed;
  font-size: 0.75rem;
  padding-bottom: 0.5rem;
  margin-bottom: -0.5rem;
  width: 100%;
}

.footer {
  justify-content: flex-start;
}
</style>
